// Het commandopalet (Ctrl+K): zoeken, springen, en de handelingen die je vaak doet.
//
// Hetzelfde venster doet twee dingen, omdat het precies dezelfde vraag is: "welke notitie
// bedoel je?" In de stand "kies" komt de gekozen notitie terug als link in de tekst, in de
// stand "zoek" open je hem gewoon.
//
// Uit het onderzoek: Obsidian's quick switcher toont recente notities zodra je hem opent
// (leeg scherm is een gemiste kans), en Evernote opent een resultaat meteen op de eerste
// treffer. Allebei overgenomen.

import { useEffect, useMemo, useRef, useState } from "react";
import { api, GeenVerbinding, type Boek, type TagRegel, type Treffer } from "../api.ts";
import { zoekInSpiegel } from "../opslag.ts";
import { Icoon, type IcoonNaam } from "./Icoon.tsx";

export type PaletActie = { naam: string; icoon: IcoonNaam; toets?: string; doe: () => void };

type Props = {
  stand: "zoek" | "kies";
  boeken: Boek[];
  recente: { id: string; titel: string }[];
  acties: PaletActie[];
  sluit: () => void;
  kiesNotitie: (id: string, titel: string, term: string) => void;
  kiesTag?: (tag: string) => void;
  kiesBoek?: (id: string) => void;
  bewaarZoekopdracht?: (vraag: string) => void;
};

const VOORBEELDEN = [
  { chip: "tag:", uitleg: "notities met een tag" },
  { chip: "boek:", uitleg: "in een notitieboek" },
  { chip: "is:taak", uitleg: "met een open taak" },
  { chip: "gewijzigd:dag-7", uitleg: "van de afgelopen week" },
  { chip: "-woord", uitleg: "zonder dat woord" }
];

export function Palet(props: Props) {
  const [vraag, setVraag] = useState("");
  const [treffers, setTreffers] = useState<Treffer[]>([]);
  const [tags, setTags] = useState<TagRegel[]>([]);
  const [duur, setDuur] = useState<number | null>(null);
  const [offline, setOffline] = useState(false);
  const [gekozen, setGekozen] = useState(0);
  const tijdRef = useRef<number | null>(null);

  useEffect(() => {
    void api.tags().then((u) => setTags(u.tags)).catch(() => setTags([]));
  }, []);

  // Escape sluit het palet ook als de cursor ergens anders staat — bijvoorbeeld nadat je met
  // de muis een rij hebt aangewezen.
  useEffect(() => {
    const opToets = (g: KeyboardEvent) => {
      if (g.key === "Escape") props.sluit();
    };
    addEventListener("keydown", opToets);
    return () => removeEventListener("keydown", opToets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tijdRef.current) window.clearTimeout(tijdRef.current);
    setGekozen(0);
    if (vraag.trim().length === 0) {
      setTreffers([]);
      setDuur(null);
      return;
    }
    tijdRef.current = window.setTimeout(async () => {
      try {
        const uitkomst = await api.zoek(vraag);
        setTreffers(uitkomst.treffers);
        setDuur(uitkomst.duurMs);
        setOffline(false);
      } catch (fout) {
        if (fout instanceof GeenVerbinding) {
          // Zoeken in de eigen kopie: losse woorden die allemaal moeten voorkomen, mét een
          // fragment eromheen. Eerder zochten we hier door de ruwe JSON, en dan gaf een zoek-
          // opdracht als "type" elke notitie als treffer.
          setOffline(true);
          setTreffers(await zoekInSpiegel(vraag));
          setDuur(null);
        }
      }
    }, 160);
  }, [vraag]);

  const woord = vraag.trim().toLowerCase();

  // Wat er te kiezen valt, op volgorde: eerst de notities, daarna tags, boeken en acties.
  const regels = useMemo(() => {
    const uit: { soort: "notitie" | "tag" | "boek" | "actie"; sleutel: string; titel: string; bij?: string; doe: () => void }[] = [];

    if (woord.length === 0) {
      for (const recent of props.recente.slice(0, 6)) {
        uit.push({
          soort: "notitie",
          sleutel: recent.id,
          titel: recent.titel || "Zonder titel",
          bij: "recent",
          doe: () => props.kiesNotitie(recent.id, recent.titel, "")
        });
      }
    } else {
      for (const treffer of treffers) {
        uit.push({
          soort: "notitie",
          sleutel: treffer.id,
          titel: treffer.titel || "Zonder titel",
          // Staat het woord alleen in een schermafdruk of pdf, dan hoort dat erbij: anders zoek
          // je je wild naar een woord dat nergens in de tekst van de notitie staat.
          bij:
            (treffer.viaBijlage ? `in ${treffer.viaBijlage}: ` : "") +
            (treffer.fragment ? treffer.fragment.replace(/\[\[|\]\]/g, "") : ""),
          doe: () => props.kiesNotitie(treffer.id, treffer.titel, vraag)
        });
      }
    }

    if (props.stand === "zoek" && woord.length > 0) {
      const losseTag = woord.replace(/^#/, "");
      for (const tag of tags.filter((t) => t.naam.includes(losseTag)).slice(0, 5)) {
        uit.push({
          soort: "tag",
          sleutel: "tag:" + tag.naam,
          titel: `#${tag.naam}`,
          bij: `${tag.aantal} notitie${tag.aantal === 1 ? "" : "s"}`,
          doe: () => props.kiesTag?.(tag.naam)
        });
      }
      const platteBoeken = props.boeken.flatMap(function plat(b): Boek[] {
        return [b, ...b.kinderen.flatMap(plat)];
      });
      for (const boek of platteBoeken.filter((b) => b.titel.toLowerCase().includes(woord.replace(/^@/, ""))).slice(0, 4)) {
        uit.push({
          soort: "boek",
          sleutel: "boek:" + boek.id,
          titel: boek.titel,
          bij: "notitieboek",
          doe: () => props.kiesBoek?.(boek.id)
        });
      }
      for (const actie of props.acties.filter((a) => a.naam.toLowerCase().includes(woord))) {
        uit.push({ soort: "actie", sleutel: actie.naam, titel: actie.naam, doe: actie.doe });
      }
    } else if (props.stand === "zoek") {
      for (const actie of props.acties) {
        uit.push({ soort: "actie", sleutel: actie.naam, titel: actie.naam, doe: actie.doe });
      }
    }

    return uit;
  }, [woord, treffers, tags, props, vraag]);

  function opToets(g: React.KeyboardEvent) {
    if (g.key === "ArrowDown") {
      g.preventDefault();
      setGekozen((n) => Math.min(n + 1, regels.length - 1));
    }
    if (g.key === "ArrowUp") {
      g.preventDefault();
      setGekozen((n) => Math.max(n - 1, 0));
    }
    if (g.key === "Enter") {
      g.preventDefault();
      regels[gekozen]?.doe();
    }
    if (g.key === "Escape") props.sluit();
  }

  return (
    <div className="overlay" onClick={props.sluit}>
      <div className="paneel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={props.stand === "kies" ? "Kies een notitie" : "Zoeken"}>
        <div className="paneelkop">
          <Icoon naam={props.stand === "kies" ? "link" : "zoek"} grootte={19} />
          <input
            autoFocus
            value={vraag}
            onChange={(e) => setVraag(e.target.value)}
            onKeyDown={opToets}
            placeholder={props.stand === "kies" ? "Naar welke notitie wil je verwijzen?" : "Zoeken, of typ een actie"}
          />
          {props.stand === "zoek" && vraag.trim() && props.bewaarZoekopdracht && (
            <button className="knop" onClick={() => props.bewaarZoekopdracht!(vraag.trim())} title="Deze zoekopdracht bewaren">
              <Icoon naam="ster" grootte={14} /> Bewaren
            </button>
          )}
        </div>

        {props.stand === "zoek" && (
          <div className="chips">
            {VOORBEELDEN.map((v) => (
              <button
                key={v.chip}
                className="chip"
                title={v.uitleg}
                onClick={() => setVraag((huidig) => (huidig ? `${huidig.trim()} ${v.chip}` : v.chip))}
              >
                {v.chip}
              </button>
            ))}
          </div>
        )}

        <div className="paneelinhoud">
          {regels.map((regel, i) => (
            <button
              key={regel.soort + regel.sleutel}
              className="rij"
              aria-current={i === gekozen}
              onMouseEnter={() => setGekozen(i)}
              onClick={regel.doe}
            >
              <span className="titel" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icoon naam={regel.soort === "tag" ? "tag" : regel.soort === "boek" ? "map" : regel.soort === "actie" ? "plus" : "notitie"} grootte={15} />
                {regel.titel}
              </span>
              {regel.bij && <span className="fragment">{regel.bij}</span>}
            </button>
          ))}
          {vraag && regels.length === 0 && <p className="leeg">Niets gevonden.</p>}
        </div>

        <div className="paneelvoet">
          <span>↑↓ kiezen</span>
          <span>↵ openen</span>
          <span>Esc sluiten</span>
          <span style={{ flexGrow: 1 }} />
          {offline ? (
            <span>offline — alleen wat op dit apparaat staat</span>
          ) : duur !== null ? (
            <span>
              {treffers.length} {treffers.length === 1 ? "resultaat" : "resultaten"} in {duur} ms
            </span>
          ) : (
            <span>tag: boek: is:taak gewijzigd:dag-7</span>
          )}
        </div>
      </div>
    </div>
  );
}
